/**
 * The text of one registry / registrar exchange laid out for reading (display only; copying
 * takes the text as exchanged). EPP and SOAP XML: one element per line, indented, an element
 * holding only text kept on its line. REST / SOAP text from the connectors' observers is a
 * request or status line and headers, a blank line, then the body: an XML body is indented, a
 * JSON body re-indented, anything else left as it is.
 */
export function prettyWire(text: string): string {
  if (text.trimStart().startsWith('<')) return prettyXml(text);
  const cut = text.indexOf('\n\n');
  if (cut < 0) return prettyJson(text) ?? text;
  const body = text.slice(cut + 2);
  return `${text.slice(0, cut)}\n\n${body.trimStart().startsWith('<') ? prettyXml(body) : (prettyJson(body) ?? body)}`;
}

function prettyJson(s: string): string | null {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return null;
  }
}

// ponytail: tokenises on `<…>`, so a `>` inside an attribute value or CDATA splits a tag; the
// registries' EPP does not use either, and the copy button keeps the exact text anyway.
export function prettyXml(xml: string): string {
  const tokens = (xml.match(/<[^>]*>|[^<]+/g) ?? []).map((t) => (t.startsWith('<') ? t : t.trim())).filter(Boolean);
  const lines: string[] = [];
  let depth = 0;
  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i] ?? '';
    const next = tokens[i + 1];
    const after = tokens[i + 2];
    const pad = '  '.repeat(depth);
    if (tok.startsWith('</')) {
      depth = Math.max(0, depth - 1);
      lines.push('  '.repeat(depth) + tok);
    } else if (!tok.startsWith('<') || /^<[?!]/.test(tok) || tok.endsWith('/>')) {
      lines.push(pad + tok);
    } else if (next?.startsWith('</')) {
      lines.push(pad + tok + next);
      i += 1;
    } else if (next !== undefined && !next.startsWith('<') && after?.startsWith('</')) {
      lines.push(pad + tok + next + after);
      i += 2;
    } else {
      lines.push(pad + tok);
      depth += 1;
    }
  }
  return lines.join('\n');
}
